import { ArrowUpRight } from "lucide-react";
import { cn } from "../../utils/cn";
import {
  CheckCircle,
  Brain,
  Database,
  Users,
  Target,
  Send,
  RefreshCw,
} from "lucide-react";

const pipelineSteps = [
  {
    id: "define",
    number: "01",
    label: "Define",
    title: "Define ICP & Strategy",
    description:
      "Define ideal customer profile, target market, buyer personas, segmentation, goals, and sales objectives.",
    icon: Target,
    color: "text-blue-400",
    bgColor: "bg-blue-400/10",
    details: [
      "ICP definition & validation",
      "Target market sizing",
      "Buyer persona development",
      "Segmentation strategy",
      "Goal setting & KPIs",
      "Sales process mapping",
    ],
  },
  {
    id: "discover",
    number: "02",
    label: "Discover",
    title: "Prospect Discovery",
    description:
      "Identify companies and prospects matching the target profile using multiple data sources and signals.",
    icon: Users,
    color: "text-purple-400",
    bgColor: "bg-purple-400/10",
    details: [
      "Company identification",
      "Contact discovery",
      "Technographic filtering",
      "Intent signal detection",
      "Lookalike modeling",
      "Market expansion",
    ],
  },
  {
    id: "enrich",
    number: "03",
    label: "Enrich & Verify",
    title: "Data Enrichment & Verification",
    description:
      "Collect relevant data, enrich records, validate contacts, and improve data quality across all touchpoints.",
    icon: Database,
    color: "text-green-400",
    bgColor: "bg-green-400/10",
    details: [
      "Multi-source enrichment",
      "Email verification",
      "Phone verification",
      "Data quality scoring",
      "Duplicate removal",
      "Continuous updates",
    ],
  },
  {
    id: "build",
    number: "04",
    label: "Build",
    title: "Campaign Architecture",
    description:
      "Create segmentation, research context, personalization logic, messaging, and campaign structure.",
    icon: Brain,
    color: "text-orange-400",
    bgColor: "bg-orange-400/10",
    details: [
      "Segmentation logic",
      "AI research context",
      "Personalization rules",
      "Message frameworks",
      "Sequence design",
      "A/B test planning",
    ],
  },
  {
    id: "reach",
    number: "05",
    label: "Reach",
    title: "Outbound Execution",
    description:
      "Deploy outbound workflows, personalized messaging, follow-ups, and response handling at scale.",
    icon: Send,
    color: "text-red-400",
    bgColor: "bg-red-400/10",
    details: [
      "Automated outreach",
      "Multi-channel sequences",
      "Smart follow-ups",
      "Response classification",
      "CRM synchronization",
      "Sales notifications",
    ],
  },
  {
    id: "optimize",
    number: "06",
    label: "Optimize",
    title: "Continuous Optimization",
    description:
      "Monitor performance, qualify responses, learn from outcomes, and continuously improve the system.",
    icon: RefreshCw,
    color: "text-teal-400",
    bgColor: "bg-teal-400/10",
    details: [
      "Performance analytics",
      "Response qualification",
      "A/B test analysis",
      "Model retraining",
      "Strategy iteration",
      "Scale planning",
    ],
  },
];

export function PipelineDiagram({
  compact = false,
  showDetails = false,
}: {
  compact?: boolean;
  showDetails?: boolean;
}) {
  return (
    <div className={cn("pipeline", compact && "pipeline-compact")}>
      <ol className="pipeline-grid" aria-label="Outbound sales process">
        {pipelineSteps.map((step) => (
          <li
            key={step.id}
            className="pipeline-card card"
            aria-label={`Step ${step.number}: ${step.title}`}
          >
            <div className="pipeline-card-top">
              <span className="step-number">{step.number}</span>
              <step.icon size={24} aria-hidden="true" />
            </div>
            <h3>{step.title}</h3>
            <p>{step.description}</p>
            {showDetails && (
              <details className="pipeline-details">
                <summary>
                  What this includes
                  <ArrowUpRight size={16} aria-hidden="true" />
                </summary>
                <ul>
                  {step.details.map((detail) => (
                    <li key={detail}>
                      <CheckCircle size={15} aria-hidden="true" />
                      <span>{detail}</span>
                    </li>
                  ))}
                </ul>
              </details>
            )}
          </li>
        ))}
      </ol>
      {!compact && (
        <div className="pipeline-feedback">
          <RefreshCw size={19} aria-hidden="true" />
          <p>
            Keep learning. Review responses and outcomes, then refine the next
            cycle.
          </p>
        </div>
      )}
    </div>
  );
}

export function PipelineStats() {
  return (
    <div className="pipeline-principles" aria-label="Principles of the system">
      {[
        {
          title: "Focused targeting",
          text: "Start with the buyers your business can serve.",
        },
        {
          title: "Verified context",
          text: "Build outreach on useful, checked data.",
        },
        {
          title: "Human oversight",
          text: "Keep strategy and quality in expert hands.",
        },
        {
          title: "Ongoing improvement",
          text: "Use real feedback to refine the system.",
        },
      ].map((item) => (
        <div key={item.title}>
          <h3>{item.title}</h3>
          <p>{item.text}</p>
        </div>
      ))}
    </div>
  );
}
